import React, { ComponentProps } from 'react';
import { SourceType } from 'dnd-core';
import { useDrop } from 'react-dnd';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import { DragItem } from '../SortableListItem/SortableListItem';
import styles from './SortableListDropTarget.module.css';

export type SortableListDropTargetProps<T> = PropsWithoutChildren<
  Omit<ComponentProps<'div'>, 'onDrop'>
> & {
  dndType: SourceType;
  previousItem: T | null;
  nextItem: T | null;
  onDrop: (dropInfo: {
    item: T;
    previousItem: T | null;
    nextItem: T | null;
  }) => void;
};

export const SortableListDropTarget = React.memo(
  <T,>(props: SortableListDropTargetProps<T>) => {
    const {
      dndType,
      previousItem,
      nextItem,
      onDrop,
      className,
      style,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });

    const [collectedProps, ref] = useDrop({
      accept: dndType,
      drop: (item: DragItem<T>) => {
        onDrop({
          item: item.item,
          previousItem,
          nextItem
        });
      },
      collect: (monitor) => {
        const canDrop = monitor.canDrop();
        const isOver = monitor.isOver();

        let itemHeight: number | undefined;

        if (canDrop) {
          const item = monitor.getItem() as DragItem<T>;
          itemHeight = item.clientHeight;
        }

        return {
          isOver,
          canDrop,
          itemHeight
        };
      }
    });

    return (
      <div
        ref={ref}
        className={classes(
          className,
          styles.container,
          collectedProps.isOver ? styles.isOver : null,
          collectedProps.canDrop ? styles.canDrop : null
        )}
        style={{
          ...style,
          height: collectedProps.isOver ? collectedProps.itemHeight : undefined
        }}
        {...remainingProps}
      >
        <div className={styles.extraDropArea}></div>
      </div>
    );
  }
) as <T>(props: SortableListDropTargetProps<T>) => JSX.Element;
